import React, { useState } from 'react';
import { Container, Row, Col, Nav, Badge } from 'react-bootstrap';
import './index.css';
import { useParams } from "react-router-dom";


// ============================================================================
// IMPORTS DES COMPOSANTS
// ============================================================================

import EditExpertise from '../../../../components/Expmanage/editExpertise';
import EditContract from '../../../../components/Contract/editContract';
import EditClaims from '../../../../components/Claims/editClaims';
import Report from '../../../../components/Report';
import Contents from '../../../../components/Contents';
import Costings from '../../../../components/Costings';
import Files from '../../../../components/Files';
import Contact from '../../../../components/Contact';
import Tasks from '../../../../components/Tasks';
import EditExpertiseModal from '../../../../components/Expmanage/EditExpertiseModal';

// ============================================================================
// CONSTANTES
// ============================================================================

const NAV_ITEMS = [
  {
    key: 'EditExpertise',
    label: 'Expertise',
    icon: '/contrat.png',
    emoji: '📋',
    component: EditExpertise
  },
  {
    key: 'Tasks',
    label: 'Suivi',
    icon: '/suivi.png',
    emoji: '✅',
    component: Tasks
  },
  {
    key: 'EditContract',
    label: 'Contrat',
    icon: '/contrat.png',
    emoji: '📝',
    component: EditContract
  },
  {
    key: 'EditClaims',
    label: 'Sinistre',
    icon: '/sinistre.png',
    emoji: '⚠️',
    component: EditClaims
  },
  {
    key: 'Contact',
    label: 'Intervenants',
    icon: '/gens.png',
    emoji: '👥',
    component: Contact
  },
  {
    key: 'Report',
    label: 'Rapport',
    icon: '/rapport.png',
    emoji: '📄',
    component: Report
  },
  {
    key: 'Costings',
    label: 'Bâtiment',
    icon: '/calculatrice.png',
    emoji: '🏗️',
    component: Costings
  },
  {
    key: 'Contents',
    label: 'Contenu',
    icon: '/calculatrice.png',
    emoji: '📦',
    component: Contents
  },
  {
    key: 'Files',
    label: 'Documents',
    icon: '/camera.png',
    emoji: '📁',
    component: Files
  }
];

const DEFAULT_ACTIVE_KEY = 'EditExpertise';

const ICON_STYLE = {
  width: '32px',
  height: '32px',
  marginRight: '8px'
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

function ManagerExpertiseCommon() {
  // -------------------------------------------------------------------------
  // États - Navigation
  // -------------------------------------------------------------------------
  const [activeKey, setActiveKey] = useState(DEFAULT_ACTIVE_KEY);
  const [showEditModal, setShowEditModal] = useState(false);
  const { expertiseId } = useParams();

  // =========================================================================
  // FONCTIONS - Helpers
  // =========================================================================

  /**
   * 🔍 Récupère le composant actif en fonction de la clé
   */
  const getActiveComponent = () => {
    const activeItem = NAV_ITEMS.find(item => item.key === activeKey);
    return activeItem ? activeItem.component : null;
  };

  /**
   * 🎨 Récupère le label actif pour l'affichage
   */
  const getActiveLabel = () => {
    const activeItem = NAV_ITEMS.find(item => item.key === activeKey);
    return activeItem ? activeItem.label : '';
  };

  /**
   * 📌 Gère le changement d'onglet
   */
  const handleNavSelect = (selectedKey) => {
    setActiveKey(selectedKey);
    // Scroll vers le haut lors du changement de section
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * 🎯 Rendu d'un item de navigation
   */
  const renderNavItem = (item) => (
    <Nav.Item key={item.key}>
      <Nav.Link 
        eventKey={item.key}
        className="d-flex align-items-center"
      >
        <img
          src={item.icon}
          alt={item.label}
          style={ICON_STYLE}
        />
        <span>{item.label}</span>
        {activeKey === item.key && (
          <Badge bg="primary" className="ms-auto">
            Actif
          </Badge>
        )}
      </Nav.Link>
    </Nav.Item>
  );

  // =========================================================================
  // RENDU PRINCIPAL
  // =========================================================================

  const ActiveComponent = getActiveComponent();

  return (
    <Container fluid>
      <Row style={{ paddingTop: '26px' }}>
        {/* ================================================================
            NAVIGATION LATÉRALE
            ================================================================ */}
        <Col 
          md={2} 
          className="bg-light border-right fixed-nav"
          style={{
            position: 'fixed',
            height: '100vh',
            overflowY: 'auto',
            paddingTop: '20px'
          }}
        >
          {/* En-tête de navigation */}
          <div className="mb-3 px-3">
            <h5 className="text-muted mb-0">📂 Navigation</h5>
            <small className="text-muted">Gestion du dossier</small>
          </div>
          <div className="px-3 mb-3">
            <button
              className="btn btn-success w-100"
              onClick={() => setShowEditModal(true)}
            >
              ✏️ Modifier l’expertise
            </button>
          </div>

          <EditExpertiseModal
            show={showEditModal}
            onHide={() => setShowEditModal(false)}
            expertiseId={expertiseId}
          />

          {/* Menu de navigation */}
          <Nav
            className="flex-column mt-2"
            variant="pills"
            activeKey={activeKey}
            onSelect={handleNavSelect}
          >
            {NAV_ITEMS.map(renderNavItem)}
          </Nav>

          {/* Footer de navigation */}
          <div className="mt-4 px-3 py-3 border-top">
            <small className="text-muted d-block">
              <strong>Section active:</strong>
            </small>
            <Badge bg="primary" className="mt-2">
              {getActiveLabel()}
            </Badge>
          </div>
        </Col>

        {/* ================================================================
            ZONE DE CONTENU PRINCIPALE
            ================================================================ */}
        <Col 
          md={10} 
          style={{ 
            marginLeft: '16.66666667%',
            minHeight: '100vh'
          }}
        >
          {/* Breadcrumb ou fil d'Ariane */}
          <div className="mb-3">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a href="#home">Accueil</a>
                </li>
                <li className="breadcrumb-item">
                  <a href="#expertises">Expertises</a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {getActiveLabel()}
                </li>
              </ol>
            </nav>
          </div>

          {/* Rendu du composant actif */}
          {ActiveComponent && <ActiveComponent />}
          
          {/* Message si aucun composant trouvé */}
          {!ActiveComponent && (
            <div className="alert alert-warning">
              <h4>⚠️ Section non trouvée</h4>
              <p>La section demandée n'existe pas ou n'est pas disponible.</p>
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
}

export default ManagerExpertiseCommon;